'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Ban, Flag, Loader2, MoreHorizontal, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { marketplaceApi, type ReportTargetType } from '@/lib/api';
import { haptics } from '@/lib/native';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ReportSheet } from './report-sheet';

export interface ReportBlockMenuProps {
  targetType: ReportTargetType;
  targetId: string;
  targetLabel?: string;
  /**
   * The counterparty business. Given, the menu also offers Block/Unblock;
   * omitted (a public visitor, or your own content), it offers Report only.
   */
  counterpartyBusinessId?: string | null;
  counterpartyName?: string | null;
  /** Called after a successful block or unblock, e.g. to refetch a thread. */
  onBlockChange?: (blocked: boolean) => void;
  /** Extra classes for the trigger button. */
  className?: string;
  /** Screen-reader label for the trigger. */
  label?: string;
  align?: 'start' | 'end';
}

/**
 * The "…" menu that puts Report — and Block, where there is a counterparty —
 * on every user-generated surface (App Store guideline 1.2).
 */
export function ReportBlockMenu({
  targetType,
  targetId,
  targetLabel,
  counterpartyBusinessId,
  counterpartyName,
  onBlockChange,
  className,
  label = 'More options',
  align = 'end',
}: ReportBlockMenuProps) {
  const [reportOpen, setReportOpen] = useState(false);
  const [confirmBlockOpen, setConfirmBlockOpen] = useState(false);
  // Radix returns focus to the trigger when the menu closes, which races the
  // dialog/sheet that the menu item just opened — and the report sheet has a
  // textarea that then cannot be typed into. Suppress that restore only when an
  // item actually handed off to an overlay; Escape still restores focus.
  const handingOffRef = useRef(false);
  // null = not looked up yet. Block is offered regardless (disabled while the
  // lookup is in flight); only Unblock waits on a confirmed `true`, so a slow or
  // failed status call can never hide the control an App Store reviewer is
  // looking for.
  const [blocked, setBlocked] = useState<boolean | null>(null);
  const [working, setWorking] = useState(false);

  // Only ask the server once the menu is actually opened — this component is
  // rendered on every review row, and a status call per row is a lot of traffic
  // for a menu most people never touch.
  const loadBlockStatus = useCallback(async () => {
    if (!counterpartyBusinessId || blocked !== null) return;
    try {
      const res = await marketplaceApi.getBlockStatus(counterpartyBusinessId);
      setBlocked(res.blocked);
    } catch {
      // Signed out, or no business context yet. Leave it unknown: Block stays
      // available and the server is the one that decides.
      setBlocked(null);
    }
  }, [counterpartyBusinessId, blocked]);

  useEffect(() => {
    setBlocked(null);
  }, [counterpartyBusinessId]);

  const doBlock = async () => {
    if (!counterpartyBusinessId) return;
    setWorking(true);
    try {
      await marketplaceApi.blockBusiness(counterpartyBusinessId);
      setBlocked(true);
      void haptics.success();
      toast.success(`Blocked${counterpartyName ? ` ${counterpartyName}` : ''}`);
      setConfirmBlockOpen(false);
      onBlockChange?.(true);
    } catch (err: any) {
      void haptics.error();
      const message: string = err?.message || 'Could not block';
      toast.error(
        /unauthor|401|403|business context/i.test(message)
          ? 'Sign in with a store to block this business'
          : message,
      );
    } finally {
      setWorking(false);
    }
  };

  const doUnblock = async () => {
    if (!counterpartyBusinessId) return;
    setWorking(true);
    try {
      await marketplaceApi.unblockBusiness(counterpartyBusinessId);
      setBlocked(false);
      void haptics.light();
      toast.success('Unblocked');
      onBlockChange?.(false);
    } catch (err: any) {
      void haptics.error();
      toast.error(err?.message || 'Could not unblock');
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <DropdownMenu
        onOpenChange={(open) => {
          if (open) {
            void haptics.light();
            void loadBlockStatus();
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={label}
            className={cn('h-8 w-8 shrink-0 text-muted-foreground', className)}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          className="w-52"
          onCloseAutoFocus={(event) => {
            if (handingOffRef.current) {
              event.preventDefault();
              handingOffRef.current = false;
            }
          }}
        >
          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={() => {
              void haptics.selection();
              handingOffRef.current = true;
              setReportOpen(true);
            }}
          >
            <Flag className="mr-2 h-4 w-4" />
            Report
          </DropdownMenuItem>

          {counterpartyBusinessId && blocked !== true && (
            <DropdownMenuItem
              className="cursor-pointer text-destructive focus:text-destructive"
              onSelect={() => {
                void haptics.warning();
                handingOffRef.current = true;
                setConfirmBlockOpen(true);
              }}
            >
              <Ban className="mr-2 h-4 w-4" />
              Block{counterpartyName ? ` ${counterpartyName}` : ''}
            </DropdownMenuItem>
          )}

          {counterpartyBusinessId && blocked === true && (
            <DropdownMenuItem
              className="cursor-pointer"
              disabled={working}
              onSelect={(e) => {
                e.preventDefault();
                void doUnblock();
              }}
            >
              <ShieldOff className="mr-2 h-4 w-4" />
              Unblock{counterpartyName ? ` ${counterpartyName}` : ''}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ReportSheet
        open={reportOpen}
        onOpenChange={setReportOpen}
        targetType={targetType}
        targetId={targetId}
        targetLabel={targetLabel}
      />

      <Dialog open={confirmBlockOpen} onOpenChange={setConfirmBlockOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Block {counterpartyName || 'this business'}?</DialogTitle>
            <DialogDescription>
              Neither of you will be able to message the other or send new offers.
              You can undo this any time from Settings → Account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmBlockOpen(false)}
              disabled={working}
            >
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={doBlock} disabled={working}>
              {working && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Block
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
